import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseX, Icons, Modal } from '../../components/studio/studioUi';
import type { VideoRender } from '../../services/video.service';
import { FONT_SERIF } from '../landing/landingData';
import { fmtBytes } from '../landing/format';
import { FORMAT_OPTIONS } from './videoUi';

/**
 * "›" button on a finished render: Lihat / Posting / Unduh. Renders are stored with
 * Content-Disposition: attachment (so Unduh saves the file), which is also why
 * opening the URL in a tab downloads it — Lihat plays it in a page <video> instead.
 */
const MENU_W = 210;
const MENU_H = 140;

export const RenderMenu: React.FC<{ render: VideoRender; onView: () => void; onPost: () => void }> = ({ render, onView, onPost }) => {
    // Portalled, fixed at the button's rect: the cards clip (overflow: hidden) and lift on
    // hover (transform), which would make a fixed child position against the card.
    const [at, setAt] = useState<{ top: number; left: number; host: Element } | null>(null);
    const box = useRef<HTMLDivElement>(null);
    const menu = useRef<HTMLDivElement>(null);
    const btn = useRef<HTMLButtonElement>(null);
    const open = !!at;
    useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent) => !box.current?.contains(e.target as Node) && !menu.current?.contains(e.target as Node) && setAt(null);
        const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAt(null);
        const away = () => setAt(null);
        document.addEventListener('mousedown', close);
        document.addEventListener('keydown', esc);
        window.addEventListener('scroll', away, true);
        window.addEventListener('resize', away);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('keydown', esc);
            window.removeEventListener('scroll', away, true);
            window.removeEventListener('resize', away);
        };
    }, [open]);
    if (render.status !== 'DONE' || !render.url) return null;
    const toggle = () => {
        if (open) return setAt(null);
        const r = btn.current!.getBoundingClientRect();
        // Right-aligned under the button; above it when there is no room below.
        const below = r.bottom + 6 + MENU_H <= window.innerHeight;
        // .hv-root carries the theme variables (and no transform); body is the fallback.
        const host = btn.current!.closest('.hv-root') ?? document.body;
        setAt({ top: below ? r.bottom + 6 : r.top - 6 - MENU_H, left: Math.max(8, Math.min(r.right - MENU_W, window.innerWidth - MENU_W - 8)), host });
    };
    const item: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '9px 11px', borderRadius: 9, border: 0, background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left', textDecoration: 'none', boxSizing: 'border-box' };
    const pick = (fn: () => void) => () => {
        setAt(null);
        fn();
    };
    return (
        // Clicks stay here: the card or row around it opens something else.
        <div ref={box} onClick={(e) => e.stopPropagation()} style={{ position: 'relative', zIndex: 1, flex: 'none' }}>
            <button ref={btn} type="button" aria-haspopup="menu" aria-expanded={open} aria-label="Lihat, posting, atau unduh" title="Lihat, posting, atau unduh" onClick={toggle} style={{ width: 28, height: 28, borderRadius: 999, border: '1px solid var(--hv-line-2)', background: open ? 'var(--hv-page-2)' : 'transparent', color: 'var(--hv-body)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icons.chevronR />
            </button>
            {at && createPortal(
                // React events bubble through portals: keep menu clicks away from the card.
                <div ref={menu} role="menu" onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', top: at.top, left: at.left, zIndex: 60, width: MENU_W, padding: 5, borderRadius: 13, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 20px 40px -20px var(--hv-shadow)', boxSizing: 'border-box' }}>
                    <button type="button" role="menuitem" className="hv-h-mint" onClick={pick(onView)} style={item}>▶ Lihat</button>
                    <button type="button" role="menuitem" className="hv-h-mint" data-track="video_post" onClick={pick(onPost)} style={item}><Icons.share size={13} /> Posting / Jadwalkan</button>
                    <a role="menuitem" className="hv-h-mint" href={render.url} download onClick={() => setAt(null)} style={item}>
                        <Icons.download /> Unduh MP4{render.bytes ? ` · ${fmtBytes(render.bytes)}` : ''}
                    </a>
                </div>,
                at.host,
            )}
        </div>
    );
};

/** Plays a render in the page (works despite the attachment header). */
export const RenderViewer: React.FC<{ render: VideoRender | null; title: string; onClose: () => void; onPost: () => void }> = ({ render, title, onClose, onPost }) => {
    const portrait = render?.format === 'portrait';
    return (
        <Modal open={!!render} onClose={onClose} maxWidth={portrait ? 460 : 900} label="Lihat video">
            {render?.url && (
                <div style={{ padding: 18, display: 'grid', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ flex: 1, minWidth: 0, fontFamily: FONT_SERIF, fontSize: 22, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {title}
                            {render.format ? ` · ${FORMAT_OPTIONS.find((f) => f.value === render.format)?.label}` : ''}
                            {render.durationSec ? ` · ${Math.round(render.durationSec)} dtk` : ''}
                        </div>
                        <CloseX onClick={onClose} />
                    </div>
                    <video
                        key={render.id}
                        src={render.url}
                        controls
                        autoPlay
                        playsInline
                        style={{ display: 'block', width: '100%', maxHeight: '70vh', aspectRatio: portrait ? '9 / 16' : render.format === 'square' ? '1 / 1' : '16 / 9', borderRadius: 12, background: '#000' }}
                    />
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <a href={render.url} download className="hv-h-mint-bd" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 14px', borderRadius: 999, border: '1px solid var(--hv-line)', color: 'var(--hv-ink)', fontSize: 13, fontWeight: 700 }}>
                            <Icons.download /> Unduh
                        </a>
                        <button type="button" data-track="video_post" onClick={onPost} className="hv-h-mint-bd" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 14px', borderRadius: 999, border: '1px solid var(--hv-line)', background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                            <Icons.share size={13} /> Posting / Jadwalkan
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
};
